import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { GalleryFolder } from '@tailoring-crm/shared';
import { galleryFolderLabel } from '../../lib/gallery/gallery-folder-label.js';
import { useMoveMediaToFolder } from '../../lib/gallery/use-gallery.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@tailoring-crm/ui';

/**
 * Filing a selection — one {@link useMoveMediaToFolder} call carrying every id, whether the
 * selection was made inside a folder or from the unfiled view. `'unfiled'` in the picker sends
 * `folderId: null`, which is what unfiles the whole batch again.
 */
export interface MoveMediaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folders: readonly GalleryFolder[];
  mediaIds: readonly string[];
  onMoved: () => void;
}

export function MoveMediaDialog({
  open,
  onOpenChange,
  folders,
  mediaIds,
  onMoved,
}: MoveMediaDialogProps): ReactElement {
  const { t } = useTranslation();
  const move = useMoveMediaToFolder();
  const [target, setTarget] = useState('unfiled');

  const submit = async (): Promise<void> => {
    await move.mutateAsync({
      mediaIds: [...mediaIds],
      folderId: target === 'unfiled' ? null : target,
    });
    onMoved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('gallery.move.title', { count: mediaIds.length })}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="gallery-move-target">{t('gallery.move.targetLabel')}</Label>
          <select
            id="gallery-move-target"
            value={target}
            onChange={(event) => setTarget(event.target.value)}
            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
          >
            <option value="unfiled">{t('gallery.unfiled')}</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {galleryFolderLabel(t, folder)}
              </option>
            ))}
          </select>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={mediaIds.length === 0 || move.isPending}
            onClick={() => void submit()}
          >
            {t('gallery.move.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
